/**
 * UniverseView: the brand universe page. The status sentence and the orbit read
 * the shape of what the brand knows and where it is thin; the next move charts
 * the gaps; the list and the fact card read single facts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ArrowRight } from "lucide-react";
import { ErrorState } from "../components/ViewStates";
import UniverseStage from "../components/UniverseStage";
import UniverseReviewPane from "../components/UniverseReviewPane";
import UniverseOutlineList from "../components/UniverseOutlineList";
import UniverseFactCard, { type FactCardGroup, type FactCardNeighbour } from "../components/UniverseFactCard";
import UniverseOnboarding from "../components/UniverseOnboarding";
import CompetitorManagerModal from "../components/CompetitorManagerModal";
import { WallViewHeader } from "../components/WallBar";
import { agentRail, useAgentFocus } from "../agent/agentFocusStore";
import type { FactProposal } from "@/types/chat.type";
import type { UniverseGraph } from "@/types/brandUniverse.type";
import {
  FirstStrategyNudgeModal,
  StrategyReadyCard,
  hasSeenFirstStrategyNudge,
  markFirstStrategyNudgeSeen,
} from "../components/FirstStrategyNudge";
import { KitConsequenceWarning, KitModal } from "../components/kit/Modal";
import { KitButton } from "../components/kit/Button";
import { useUniverse, useBrandIdentity } from "../data/useCreativeOsHome";
import { useBrandUniversePendingFactProposals } from "../api/useBrandUniversePendingFactProposals";
import { useStrategies } from "../strategy/api/useStrategies";
import { useDeleteBrandUniverseFact } from "../api/useDeleteBrandUniverseFact";
import type { RingArc, StageFact } from "../data/universeLayout";
import { buildUniverseLayout, computeUniverseMaturity, listUniverseConcepts } from "../data/universeLayout";
import { buildUniverseOutline, findOutlineFactPath } from "../data/universeOutline";
import { computeSubjectGaps, nextMove, statusSentence } from "../data/universeStatus";
import { placeProposal } from "../data/proposalPlacement";
import { formatDayMonth, stripFactPrefix } from "../data/universeText";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { useRealtimeEvent } from "@/lib/socket";
import { universeActivityToast } from "../api/universeRealtime";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import { useUploadBrandLogo } from "@/hooks/useUploadBrandLogo";
import { queue } from "@/components/Toast";
import { getAiRequestErrorMessage } from "@/lib/api";
import "../universe/universe.css";

const E = "cubic-bezier(0.16,1,0.3,1)";

/** How a proposal reads back to the agent when the user asks for a revision. */
function formatProposalText(proposal: FactProposal): string {
  const name = proposal.factName?.trim();
  const description = proposal.factDescription?.trim();
  if (name && description) return `${name}: ${description}`;
  return proposal.factText;
}

/**
 * What the dev fixture harness hands in (/design/universe-preview). With it, the
 * live reads are switched off at the source and every write is a no-op that
 * says so. Live mounts never pass it.
 */
export type UniversePreview = {
  graph: UniverseGraph;
  isLoading: boolean;
  isError: boolean;
  /** The previous visit's last-seen stamp, in place of localStorage. */
  lastSeen: string | null;
  canEdit: boolean;
  isGuest: boolean;
  brand: { id: string; name: string; initial: string; logoUrl: string | null };
  /** Pending proposals, for review mode. */
  proposals?: FactProposal[];
  /** The sun shows the logo upload in flight. */
  uploadingLogo?: boolean;
  /** Open the first-run onboarding over the page. */
  onboarding?: boolean;
};

const NOOP = () => {};

export default function UniverseView({ dark, preview }: { dark: boolean; preview?: UniversePreview }) {
  // A Guest (a brand's client) reads the universe: no proposals queue, no
  // strategy nudge, no brand agent to ask and nothing to delete.
  const livePermissions = usePermissions();
  const { canEdit, isGuest } = preview ?? livePermissions;
  const compact = useMediaQuery("(max-width: 1023px)");
  const liveUniverse = useUniverse({ enabled: !preview });
  const previewGraph = preview?.graph;
  const previewLayout = useMemo(() => (previewGraph ? buildUniverseLayout(previewGraph) : null), [previewGraph]);
  const universe = preview && previewLayout
    ? {
        graph: preview.graph,
        ...previewLayout,
        total: previewLayout.facts.length,
        isLoading: preview.isLoading,
        isError: preview.isError,
        isFetching: preview.isLoading,
        refetch: NOOP,
      }
    : liveUniverse;
  const { graph, facts, rings, subjects, total, isLoading, isError, isFetching, refetch } = universe;
  const conceptOptions = useMemo(
    () => listUniverseConcepts(graph).map((c) => ({ id: c.id, name: c.name })),
    [graph],
  );

  // The Assembly plays once per session per brand.
  const storeBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const selectedBrandId = preview ? preview.brand.id : storeBrandId;
  const askUniverse = useCallback(
    (text: string, opts?: { newChat?: boolean }) => {
      if (preview) {
        queue.add({ title: "Preview: this goes to the agents", description: text, variant: "info" });
        return;
      }
      agentRail.prefill(text, opts);
    },
    [preview],
  );
  const livePending = useBrandUniversePendingFactProposals(isGuest || preview ? null : selectedBrandId);
  const pendingProposals = preview ? (preview.proposals ?? []) : livePending.pending;
  const proposalsLoading = preview ? false : livePending.isLoading;
  // First-strategy hand-off: once the universe is mature and the brand has no
  // strategy yet, a one-time popup plus a persistent rail card point at the
  // strategy page.
  const { strategies, isLoading: strategiesLoading } = useStrategies(selectedBrandId, {
    enabled: !isGuest && !preview,
  });
  const [reviewingProposals, setReviewingProposals] = useState(false);
  const [reviewCurrent, setReviewCurrent] = useState<FactProposal | null>(null);
  const [managingCompetitors, setManagingCompetitors] = useState(false);
  const [strategyNudgeClosed, setStrategyNudgeClosed] = useState(false);
  const [nudgeBrand, setNudgeBrand] = useState(selectedBrandId);
  const deleteFact = useDeleteBrandUniverseFact(selectedBrandId);
  const uploadLogo = useUploadBrandLogo(selectedBrandId);
  const [factPendingDelete, setFactPendingDelete] = useState<StageFact | null>(null);
  // First-run onboarding: shown whenever the universe has < 3 facts. Dismissal is
  // NOT persisted. Reset when the brand changes so a newly created (empty) brand
  // still gets the takeover.
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  if (nudgeBrand !== selectedBrandId) {
    setNudgeBrand(selectedBrandId);
    setStrategyNudgeClosed(false);
    setOnboardingDismissed(false);
  }
  // The onboarding greets the brand by name, so it waits for the brand list to
  // SETTLE before opening (settled, not successful: on error it opens nameless).
  const { data: brands, isPending: brandsPending } = useGetBrands({ enabled: !preview });
  const resolvedBrandName = brands?.find((b) => b.id === selectedBrandId)?.name;

  const [intro] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    if (!selectedBrandId) return true;
    try {
      return !sessionStorage.getItem(`co:universe:assembled:v1:${selectedBrandId}`);
    } catch {
      return true;
    }
  });
  // The mark is written here, never in the initializer: StrictMode double-invokes
  // initializers. This write is idempotent.
  useEffect(() => {
    if (!intro || !selectedBrandId) return;
    try {
      sessionStorage.setItem(`co:universe:assembled:v1:${selectedBrandId}`, "1");
    } catch {
      /* best-effort; a blocked sessionStorage just replays the intro */
    }
  }, [intro, selectedBrandId]);

  // Similarity edges between staged facts, weight-sorted, capped at 5 per fact.
  // The stage draws them as lines on hover; the card lists them as "Close to".
  const related = useMemo(() => {
    const staged = new Set(facts.map((f) => f.id));
    const weighted: Record<string, Array<{ id: string; w: number }>> = {};
    for (const e of graph.edges) {
      if (e.kind !== "similarity") continue;
      if (!staged.has(e.sourceId) || !staged.has(e.targetId)) continue;
      const w = e.weight ?? 0;
      (weighted[e.sourceId] ??= []).push({ id: e.targetId, w });
      (weighted[e.targetId] ??= []).push({ id: e.sourceId, w });
    }
    const out: Record<string, string[]> = {};
    for (const [id, rels] of Object.entries(weighted)) {
      out[id] = rels.sort((a, b) => b.w - a.w).slice(0, 5).map((r) => r.id);
    }
    return out;
  }, [graph, facts]);

  // Maturity still decides the strategy hand-off; it is no longer printed.
  const maturity = useMemo(() => computeUniverseMaturity(facts, rings, subjects), [facts, rings, subjects]);

  // Readable outline: subject -> named entity (for competitors) -> facet -> fact.
  const factGroups = useMemo(
    () => buildUniverseOutline(graph, facts, subjects),
    [graph, facts, subjects],
  );
  const factById = useMemo(() => new Map(facts.map((f) => [f.id, f] as const)), [facts]);
  const arcByKey = useMemo(() => {
    const out = new Map<string, RingArc>();
    rings.forEach((ring) => ring.arcs.forEach((arc) => out.set(arc.key, arc)));
    return out;
  }, [rings]);
  const gaps = useMemo(() => computeSubjectGaps(rings), [rings]);
  // Every group each fact is filed in (primary, same-subject echoes and
  // cross-subject filings), for the list's "also in" caption and the card.
  const filings = useMemo(() => {
    const out = new Map<string, Array<{ key: string; label: string; subjectKey: string; subject: string }>>();
    rings.forEach((ring) => {
      const subject = ring.label.charAt(0) + ring.label.slice(1).toLowerCase();
      ring.arcs.forEach((arc) =>
        arc.factIds.forEach((id) => {
          const list = out.get(id) ?? [];
          list.push({ key: arc.key, label: arc.label, subjectKey: ring.id, subject });
          out.set(id, list);
        }),
      );
    });
    return out;
  }, [rings]);
  /** "Never do", or "Culture · Moments" when the other filing is in another
   *  subject. `hereKey` is the group being read; `hereSubject` its subject. */
  const otherFilings = useCallback(
    (factId: string, hereKey: string, hereSubject: string | undefined) =>
      (filings.get(factId) ?? [])
        .filter((f) => f.key !== hereKey && f.key !== `${hereKey}:general`)
        .map((f) => (f.subjectKey === hereSubject ? f.label : `${f.subject} · ${f.label}`)),
    [filings],
  );
  const alsoIn = useCallback(
    (factId: string, groupKey: string) => {
      const here = (filings.get(factId) ?? []).find((f) => f.key === groupKey || f.key === `${groupKey}:general`);
      const subject = here?.subjectKey ?? factById.get(factId)?.subjectKey;
      return otherFilings(factId, groupKey, subject).join(", ");
    },
    [filings, factById, otherFilings],
  );
  // With proposals waiting, the review strip under the header points at them
  // and opens review mode (Universe / Review proposed facts).
  const reviewable = canEdit && !!selectedBrandId && pendingProposals.length > 0;
  const sentence = useMemo(() => statusSentence(total, gaps), [total, gaps]);
  const move = useMemo(() => nextMove(gaps), [gaps]);
  const conceptNameById = useMemo(() => new Map(conceptOptions.map((c) => [c.id, c.name])), [conceptOptions]);
  const placementOf = useCallback(
    (p: FactProposal) => placeProposal(p, rings, conceptNameById),
    [rings, conceptNameById],
  );
  const landsHere = useMemo(() => {
    if (!reviewingProposals || !reviewCurrent) return null;
    const place = placementOf(reviewCurrent);
    if (!place.arcKey) return null;
    return { arcKey: place.arcKey, label: place.path.slice(1).join(" › ") || place.path.join(" › ") };
  }, [reviewingProposals, reviewCurrent, placementOf]);

  // Hover-sync, both ways. A hovered fact ROW lights its planet; the stage's own
  // hovered (or keyboard-active) planet lights its row.
  const [hoverFactId, setHoverFactId] = useState<string | null>(null);
  const [stageHoverId, setStageHoverId] = useState<string | null>(null);
  // Rings lit from the list, and the ring the pointer lights on stage. A phone
  // cannot hover, so there the subject you opened keeps its ring lit until
  // its row closes.
  const [listHoverSubject, setListHoverSubject] = useState<string | null>(null);
  const [openedSubject, setOpenedSubject] = useState<string | null>(null);
  const tappedSubject = compact ? openedSubject : null;
  const [stageHoverRing, setStageHoverRing] = useState<string | null>(null);
  // Orbit index expansion, collapsed by default: the shape first.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const activeFactId = hoverFactId ?? stageHoverId;
  const litSubjectKey =
    stageHoverRing ??
    (activeFactId ? factById.get(activeFactId)?.subjectKey ?? null : null) ??
    listHoverSubject ??
    tappedSubject;

  // The opened fact. ?fact=<id> makes it addressable: a refresh, a copied link
  // and the agent's Navigate all land on the same fact.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFactId, setSelectedFactId] = useState<string | null>(
    () => searchParams.get("fact"),
  );
  const urlFactId = searchParams.get("fact");
  const [lastUrlFactId, setLastUrlFactId] = useState(urlFactId);
  if (urlFactId !== lastUrlFactId) {
    setLastUrlFactId(urlFactId);
    if (urlFactId) setSelectedFactId(urlFactId);
  }
  const selectedFact = selectedFactId ? factById.get(selectedFactId) ?? null : null;
  const factOpen = !!selectedFact;
  // The rail list arrives with the page once. Coming back after a fact's card
  // it only fades in: re-showing it restarts its CSS animation, and the
  // arrival's delays made the list take over a second to return.
  const [factSeen, setFactSeen] = useState(factOpen);
  if (factOpen && !factSeen) setFactSeen(true);
  const railIn = (delay: string) =>
    factSeen ? `coFadeIn .18s ${E} both` : `coRiseT .8s ${E} ${delay} both`;

  // A ?fact= that no longer resolves (deleted, merged, or a stale link) is
  // cleared once the graph has settled, so the URL and the agent's focus stop
  // reporting it. Never mid-fetch: a fact the agent just created may not be in
  // the cache yet.
  useEffect(() => {
    if (!selectedFactId || isLoading || isError || isFetching) return;
    if (!factById.has(selectedFactId)) setSelectedFactId(null);
  }, [selectedFactId, isLoading, isError, isFetching, factById]);

  // The URL follows the selection: written on open, cleared on close, replaced
  // rather than pushed so Back leaves the page instead of walking facts.
  useEffect(() => {
    if ((searchParams.get("fact") ?? null) === selectedFactId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedFactId) next.set("fact", selectedFactId);
        else next.delete("fact");
        return next;
      },
      { replace: true },
    );
  }, [selectedFactId, searchParams, setSearchParams]);

  // Where focus goes back to when the card closes: whatever held it before the
  // first fact opened. Stepping and opening a neighbour keep the first target.
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const selectedIdRef = useRef(selectedFactId);
  useEffect(() => {
    selectedIdRef.current = selectedFactId;
  }, [selectedFactId]);
  const openFact = useCallback((id: string | null) => {
    if (id && !selectedIdRef.current && document.activeElement instanceof HTMLElement) {
      returnFocusRef.current = document.activeElement;
    }
    selectedIdRef.current = id;
    setSelectedFactId(id);
    // Opening hides the list under the pointer and closing brings it back, so
    // no row ever sees the pointer leave: drop the list's hover both ways, or
    // the planet stays lit after X (user report, 12 Oct).
    setHoverFactId(null);
    setListHoverSubject(null);
  }, []);
  const prevOpenRef = useRef(factOpen);
  useEffect(() => {
    const was = prevOpenRef.current;
    prevOpenRef.current = factOpen;
    if (factOpen || !was) return;
    // The desk card is a region, so focus is put back by hand. The phone sheet
    // is a modal that restores focus itself.
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (compact) return;
    const target =
      back && back.isConnected && back !== document.body
        ? back
        : document.querySelector<HTMLElement>(".co-universe-orbit");
    target?.focus({ preventScroll: true });
  }, [factOpen, compact]);

  useAgentFocus(
    { surface: "universe", ...(selectedFactId ? { factId: selectedFactId } : {}) },
    { contained: true },
  );

  // Teammates' changes arrive over the socket (the cache is already updated by
  // then). A fact deleted elsewhere must not stay open or pending deletion here.
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  useRealtimeEvent((event) => {
    if (event.brandId !== selectedBrandId) return;
    if (event.type === "universe.facts.removed") {
      const gone = new Set(event.data.facts.map((f) => f.id));
      setSelectedFactId((cur) => (cur && gone.has(cur) ? null : cur));
      setFactPendingDelete((cur) => (cur && gone.has(cur.id) ? null : cur));
    }
    const toast = universeActivityToast(event, viewerId);
    if (toast) queue.add({ ...toast, variant: "info" });
  });
  // On selection, open the list group that holds the fact, so closing the card
  // lands on its row.
  useEffect(() => {
    if (!selectedFactId) return;
    // A cross-filed fact lists under two subjects; open the one it lives in.
    const home = factById.get(selectedFactId)?.subjectKey;
    const path =
      findOutlineFactPath(factGroups.filter((g) => g.subjectKey === home), selectedFactId) ??
      findOutlineFactPath(factGroups, selectedFactId);
    if (!path) return;
    setOpenGroups((prev) => {
      if (path.every((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      path.forEach((key) => next.add(key));
      return next;
    });
  }, [selectedFactId, factGroups, factById]);

  // Arrival glint: facts learned since the LAST VISIT (local last-seen marker,
  // per brand). The previous stamp is kept, because the readout names it.
  const [freshIds, setFreshIds] = useState<string[]>([]);
  const [freshSince, setFreshSince] = useState<string | null>(null);
  const seenDoneRef = useRef<string | null>(null);
  useEffect(() => {
    const brandId = graph.nodes[0]?.brandId;
    if (!facts.length || !brandId || seenDoneRef.current === brandId) return;
    seenDoneRef.current = brandId;
    let last: string | null;
    if (preview) {
      last = preview.lastSeen;
    } else {
      const key = `co:universe:lastSeen:${brandId}`;
      last = localStorage.getItem(key);
      localStorage.setItem(key, new Date().toISOString());
    }
    if (last) {
      const since = last;
      setFreshIds(facts.filter((f) => f.learnedAt > since).map((f) => f.id));
      setFreshSince(since);
    }
  }, [graph, facts, preview]);
  const liveBrand = useBrandIdentity({ enabled: !preview });
  const brand = preview?.brand ?? liveBrand;

  const newestLearned = useMemo(
    () => facts.reduce<string | null>((max, f) => (!max || f.learnedAt > max ? f.learnedAt : max), null),
    [facts],
  );

  if (isLoading) return <UniverseSkeleton compact={compact} canEdit={canEdit} />;
  // Universe / Could not load: the header stays and says so; the state sits at
  // the top left of the body.
  if (isError)
    return (
      <div className="co-universe-page">
        <div className="co-universe-head">
          <WallViewHeader title="Universe" subhead={<span>Couldn’t read this universe.</span>} />
        </div>
        <div className="co-universe-error">
          <ErrorState
            align="start"
            onRetry={refetch}
            title="The universe did not load"
            body="The agents are reachable, but this request failed, so nothing is shown rather than guessed. Your facts are where you left them."
          />
        </div>
      </div>
    );

  const readout =
    freshIds.length > 0 && freshSince
      ? `${freshIds.length} new since ${formatDayMonth(freshSince)}`
      : newestLearned
        ? `Last learned ${formatDayMonth(newestLearned)}`
        : "";

  // Onboarding is for a universe with nothing in it AND nothing on the way.
  const showOnboarding = preview
    ? !!preview.onboarding && !onboardingDismissed
    : !!selectedBrandId &&
    canEdit &&
    !onboardingDismissed &&
    total < 3 &&
    pendingProposals.length === 0 &&
    !proposalsLoading &&
    !brandsPending;

  // "Universe is mature, no strategy yet": the state both nudges key off.
  const strategyReady =
    !preview &&
    !!selectedBrandId &&
    canEdit &&
    maturity.tier === "mature" &&
    !strategiesLoading &&
    strategies.length === 0;
  const showStrategyNudge =
    strategyReady &&
    !!selectedBrandId &&
    !intro &&
    !reviewingProposals &&
    !strategyNudgeClosed &&
    !hasSeenFirstStrategyNudge(selectedBrandId);
  const closeStrategyNudge = () => {
    if (selectedBrandId) markFirstStrategyNudgeSeen(selectedBrandId);
    setStrategyNudgeClosed(true);
  };

  // ---- the open fact's card ----
  const arcLabel = (f: StageFact) => {
    const arc = f.groupKey ? arcByKey.get(f.groupKey) : undefined;
    if (!arc) return f.ring < 0 ? "Unsorted" : f.concept;
    return arc.entityName ? `${arc.entityName} · ${arc.label}` : arc.label;
  };
  let cardGroup: FactCardGroup | null = null;
  let alsoFiledUnder: string[] = [];
  let closeTo: FactCardNeighbour[] = [];
  let crumbs: string[] = [];
  if (selectedFact) {
    const arc = selectedFact.groupKey ? arcByKey.get(selectedFact.groupKey) : undefined;
    if (arc) {
      cardGroup = {
        key: arc.key,
        label: arc.label,
        facts: arc.factIds.map((id) => factById.get(id)).filter((f): f is StageFact => !!f),
      };
      alsoFiledUnder = otherFilings(selectedFact.id, arc.key, arc.subjectKey);
    } else {
      const unsorted = factGroups.find((g) => g.key === "concept:__unassigned");
      cardGroup = unsorted ? { key: unsorted.key, label: "Unsorted", facts: unsorted.facts } : null;
    }
    closeTo = (related[selectedFact.id] ?? [])
      .map((id) => factById.get(id))
      .filter((f): f is StageFact => !!f)
      .slice(0, 2)
      .map((f) => ({ fact: f, groupLabel: arcLabel(f) }));
    crumbs = selectedFact.path ? selectedFact.path.split(" · ") : ["Unsorted"];
  }
  const stepFact = (dir: 1 | -1) => {
    const members = cardGroup?.facts ?? [];
    const at = members.findIndex((f) => f.id === selectedFactId);
    if (members.length < 2 || at < 0) return;
    openFact(members[(at + dir + members.length) % members.length].id);
  };
  const askAboutFact = (f: StageFact) =>
    askUniverse(`About this brand-universe fact (${f.concept}):\n"${f.label}"\n${f.desc}\n\nMy question: `);
  /** Universe / Delete a fact: what leaves, and what its group keeps. */
  const deleteDescription = (f: StageFact) => {
    const name = stripFactPrefix(f.label, f.concept);
    const arc = f.groupKey ? arcByKey.get(f.groupKey) : undefined;
    if (!arc) return `“${name}” leaves the universe.`;
    const others = arc.factIds.filter((id) => id !== f.id).length;
    return others === 0
      ? `“${name}” leaves the universe. ${arc.label} will have no facts.`
      : `“${name}” leaves the universe. ${arc.label} keeps its other ${countWord(others)} fact${others === 1 ? "" : "s"}.`;
  };
  const askReconcile = (a: StageFact, b: StageFact) =>
    askUniverse(
      `Reconcile these two brand-universe facts. Their names are near-identical but their claims differ.\n\n"${a.label}"\n${a.desc}\n\n"${b.label}"\n${b.desc}`,
    );

  const factCard = selectedFact ? (
    <UniverseFactCard
      fact={selectedFact}
      crumbs={crumbs}
      group={cardGroup}
      alsoFiledUnder={alsoFiledUnder}
      closeTo={closeTo}
      compact={compact}
      onStep={stepFact}
      onClose={() => openFact(null)}
      onOpenFact={(id) => openFact(id)}
      onAsk={isGuest ? undefined : () => askAboutFact(selectedFact)}
      onReconcile={canEdit ? (other) => askReconcile(selectedFact, other) : undefined}
      onDelete={canEdit ? () => setFactPendingDelete(selectedFact) : undefined}
      deleting={deleteFact.isPending && deleteFact.variables === selectedFact.id}
    />
  ) : null;

  const toggleGroup = (key: string) => {
    const opening = !openGroups.has(key);
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!key.startsWith("subject:")) return;
    const subject = key.slice("subject:".length);
    if (opening) setOpenedSubject(subject);
    else setOpenedSubject((current) => (current === subject ? null : current));
  };

  return (
    <div className="co-universe-page" data-fact-open={factOpen || undefined}>
      {showOnboarding ? (
        <UniverseOnboarding
          brandName={preview ? preview.brand.name : resolvedBrandName}
          onClose={() => setOnboardingDismissed(true)}
          onSubmit={(message) => {
            setOnboardingDismissed(true);
            if (preview) {
              queue.add({ title: "Preview: this goes to the agents", description: message, variant: "info" });
              return;
            }
            // Bootstrapping the universe is the one turn worth the strongest
            // setup: always a clean session, on Opus 5.5 at xhigh.
            agentRail.send(message, {
              modelId: "claude-opus-5-5",
              reasoningEffort: "xhigh",
              newSession: true,
            });
          }}
        />
      ) : null}

      {/* Mounted (not conditionally rendered) so RAC can play its exit
          animation when the nudge closes. */}
      <FirstStrategyNudgeModal isOpen={showStrategyNudge} onClose={closeStrategyNudge} />

      <div className="co-universe-head" style={{ animation: `coRiseT .8s ${E} ${intro ? "1.95s" : ".12s"} both` }}>
        <WallViewHeader
          title="Universe"
          subhead={sentence.map((part, i) =>
            part.strong ? (
              <b key={i}>{part.text}</b>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        >
          {readout ? <span className="co-universe-readout">{readout}</span> : null}
        </WallViewHeader>
      </div>

      {/* Proposals sit in a tray until someone accepts them; this row says so
          plainly, so a pending fact is never taken for a saved one. */}
      {/* Stays put while the review pane is open, so the layout never shifts. */}
      {reviewable ? (
        <button type="button" className="co-universe-review-strip" onClick={() => setReviewingProposals(true)}>
          <span className="co-universe-review-pulse" aria-hidden />
          <span className="co-universe-review-strip-text">
            <b>
              {pendingProposals.length} proposed fact{pendingProposals.length === 1 ? "" : "s"}
            </b>{" "}
            {pendingProposals.length === 1 ? "waits" : "wait"} for your review.
          </span>
          <span className="co-universe-review-strip-cta">
            Review <ArrowRight aria-hidden className="size-3.5" />
          </span>
        </button>
      ) : null}

      <div className="co-universe-split">
        {/* Left column: the orbit and its next move */}
        <section className="co-universe-hero" aria-label="Orbit">
          <UniverseStage
            facts={facts}
            rings={rings}
            related={related}
            freshIds={freshIds}
            brandInitial={brand.initial}
            logoUrl={brand.logoUrl}
            onUploadLogo={
              !canEdit
                ? undefined
                : preview
                  ? () => queue.add({ title: "Preview: nothing was uploaded", variant: "info" })
                  : (file) =>
                      uploadLogo.mutate(file, {
                        onError: (error) =>
                          queue.add({
                            title: "Couldn't upload the logo",
                            description: getAiRequestErrorMessage(error, "Please try again."),
                            variant: "error",
                          }),
                      })
            }
            uploadingLogo={uploadLogo.isPending || !!preview?.uploadingLogo}
            landsHere={landsHere}
            density="standard"
            motion
            highlightFactId={hoverFactId}
            onHoverFact={setStageHoverId}
            litRingId={listHoverSubject ?? tappedSubject}
            onHoverRing={setStageHoverRing}
            selectedFactId={selectedFactId}
            onSelectFact={openFact}
            intro={intro}
            dark={dark}
            showControls={false}
            showFactList
            containAllFacts
            allowVerticalPan={compact}
            compact={compact}
          />

          {/* Review mode draws no next move (the pane holds the one decision),
              but keeps its room so the orbit does not move. */}
          {move && (canEdit || total > 0) ? (
            <div
              className="co-universe-move"
              inert={reviewingProposals && reviewable && !compact}
              data-hidden={(reviewingProposals && reviewable && !compact) || undefined} style={{ animation: `coRiseT .8s ${E} ${intro ? "2.2s" : ".3s"} both` }}>
              <p className="co-universe-move-detail">{move.detail}</p>
              {canEdit ? (
                <button
                  type="button"
                  className="co-universe-cta"
                  data-variant={factOpen && !compact ? "outline" : undefined}
                  onClick={() => askUniverse(move.prompt, { newChat: true })}
                >
                  {move.cta}
                  <ArrowRight size={14} strokeWidth={2} aria-hidden />
                </button>
              ) : null}
            </div>
          ) : null}

          {factPendingDelete ? (
            <KitModal
              isOpen
              onOpenChange={(open) => { if (!open) setFactPendingDelete(null); }}
              title="Delete this fact?"
              description={deleteDescription(factPendingDelete)}
            >
              <div className="flex w-full min-w-0 flex-col">
                <div className="pt-3">
                  <KitConsequenceWarning />
                </div>
                <div className="mt-9 flex justify-end gap-3">
                  <KitButton type="button" variant="ghost" onClick={() => setFactPendingDelete(null)}>
                    Keep fact
                  </KitButton>
                  <KitButton
                    type="button"
                    variant="danger"
                    onClick={() => {
                      if (preview) {
                        queue.add({ title: "Preview: nothing was deleted", variant: "info" });
                        setFactPendingDelete(null);
                        return;
                      }
                      // Optimistic: the fact is already gone from the cache, and
                      // the mutation restores it with a toast if the server refuses.
                      const id = factPendingDelete.id;
                      deleteFact.mutate(id);
                      setFactPendingDelete(null);
                      setSelectedFactId((cur) => (cur === id ? null : cur));
                    }}
                  >
                    Delete fact
                  </KitButton>
                </div>
              </div>
            </KitModal>
          ) : null}
        </section>

        {reviewingProposals && reviewable && selectedBrandId ? (
          <UniverseReviewPane
            brandId={selectedBrandId}
            pending={pendingProposals}
            concepts={conceptOptions}
            placementOf={placementOf}
            compact={compact}
            preview={!!preview}
            onClose={() => setReviewingProposals(false)}
            onCurrentChange={setReviewCurrent}
            onAskAiToRevise={(proposal, instruction) => {
              if (preview) {
                queue.add({ title: "Preview: this goes to the agents", description: instruction, variant: "info" });
                return;
              }
              agentRail.send(`About the proposal "${formatProposalText(proposal)}": ${instruction}`, {
                sessionId: proposal.sessionId,
              });
            }}
          />
        ) : null}

        {/* RIGHT: the rail. The list (with proposals and the strategy card above
            it), or, while a fact is open on the desk, only that fact's card. */}
        <div className="co-universe-rail">
          {factOpen && !compact ? factCard : null}
          <div className="co-universe-rail-list" hidden={factOpen && !compact}>

            {/* Persistent next-step strip: stays as long as the universe is
                mature and the brand has no strategy. */}
            {strategyReady ? (
              <div style={{ flex: "none", marginBottom: 20, animation: railIn(intro ? "2.35s" : ".45s") }}>
                <StrategyReadyCard />
              </div>
            ) : null}

            <section aria-labelledby="co-universe-in-orbit">
              <div className="co-universe-list-head" style={{ animation: railIn(intro ? "2.5s" : ".6s") }}>
                <h2 id="co-universe-in-orbit" className="co-universe-list-title">In orbit</h2>
                {canEdit && Array.isArray(graph.entities) ? (
                  <button
                    type="button"
                    onClick={() => setManagingCompetitors(true)}
                    className="co-universe-manage font-mono text-[9.5px] font-semibold uppercase tracking-[.06em] text-co-accent-ink hover:text-co-accent"
                  >
                    Manage competitors
                  </button>
                ) : null}
              </div>
              <UniverseOutlineList
                groups={factGroups}
                openGroups={openGroups}
                onToggleGroup={toggleGroup}
                activeFactId={activeFactId}
                selectedFactId={selectedFactId}
                onSelectFact={openFact}
                onHoverFact={setHoverFactId}
                litSubjectKey={litSubjectKey}
                onHoverSubject={setListHoverSubject}
                alsoIn={alsoIn}
                arrival={factSeen ? null : { base: intro ? 2.65 : 0.75 }}
              />
              {total === 0 && !canEdit ? (
                <p className="co-universe-empty-note">
                  Nothing is charted yet. An editor starts the universe from the brand’s website, then the agents research the rest.
                </p>
              ) : null}
            </section>
          </div>
        </div>
      </div>

      {factOpen && compact ? factCard : null}

      {selectedBrandId && Array.isArray(graph.entities) ? (
        <CompetitorManagerModal
          brandId={selectedBrandId}
          entities={graph.entities}
          facts={facts}
          isOpen={managingCompetitors}
          onOpenChange={setManagingCompetitors}
          preview={!!preview}
        />
      ) : null}
    </div>
  );
}

/**
 * The page's shape while the graph loads (Universe / Loading 1:49081): the real
 * title with no status line, the five rings as the resting orbit draws them
 * (tilted, so their centres climb towards the sun), a disc for the sun, the
 * next move's bar and button block, and the list head over five row bars.
 */
const SKELETON_RINGS = [
  { cx: 432.5, cy: 410, rx: 109.5, ry: 85 },
  { cx: 426.5, cy: 422, rx: 181.5, ry: 143 },
  { cx: 420.5, cy: 433, rx: 253.5, ry: 203 },
  { cx: 414.5, cy: 445, rx: 325.5, ry: 263.5 },
  { cx: 409.5, cy: 458, rx: 397.5, ry: 326 },
];

function UniverseSkeleton({ compact, canEdit }: { compact: boolean; canEdit: boolean }) {
  return (
    <div className="co-universe-page co-universe-skeleton" role="status" aria-label="Loading the universe">
      <div className="co-universe-head">
        <WallViewHeader title="Universe" />
      </div>
      <div className="co-universe-split">
        <section className="co-universe-hero" aria-hidden="true">
          <div className="co-stage-wrap co-universe-skel-stage">
            <svg viewBox={compact ? "4 105 842 652" : "0 0 842 900"} preserveAspectRatio="xMidYMid meet" className="co-universe-skel-orbit">
              {SKELETON_RINGS.map((r) => (
                <ellipse key={r.rx} cx={r.cx} cy={r.cy} rx={r.rx} ry={r.ry} fill="none" stroke="var(--co-line)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
              <circle cx={433} cy={410} r={24} fill="var(--co-line)" />
            </svg>
          </div>
          <div className="co-universe-move">
            <span className="co-universe-skel-bar" style={{ width: "min(520px, 70%)", height: 12 }} />
            <span className="co-universe-skel-bar" style={{ width: 150, height: 40 }} />
          </div>
        </section>
        <div className="co-universe-rail" aria-hidden="true">
          <div className="co-universe-list-head">
            <span className="co-universe-list-title">In orbit</span>
            {canEdit ? (
              <span className="co-universe-manage inline-flex items-center font-mono text-[9.5px] font-semibold uppercase tracking-[.06em] text-co-accent-ink">
                Manage competitors
              </span>
            ) : null}
          </div>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="co-universe-skel-row">
              <span className="co-universe-skel-bar" data-row style={{ width: 70, height: 10 }} />
              <span className="co-universe-skel-bar" data-row style={{ width: 14, height: 10 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
/** Small counts read as words in prose ("its other three facts"). */
function countWord(n: number): string {
  return COUNT_WORDS[n] ?? String(n);
}
